/**
 * Human Resources · home.
 *
 * Where HR staff start: find anyone by typing, the everyday jobs as big
 * buttons, who is in today, what is waiting for a decision, and the
 * birthdays, anniversaries and expiries coming up. Every number opens what
 * it counts.
 */
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useHrAttendanceDay, useHrPeople, useHrRecords } from "../../api/queries";
import { useSession } from "../../app/SessionContext";
import { useScreenContext } from "../../assistant/screen";
import { Card, EmptyState, Skeleton } from "../../design/components";
import { ATTENDANCE_BY, comingUp, DATE_LABEL, LEAVE_BY, leaveDays, leaveType } from "../../domain/hrFile";
import { t } from "../../i18n";
import { Page } from "../../layout/AppShell";
import { useHrAccess } from "./access";
import { EmployeeDialog } from "./EmployeeDialog";
import { ImportDialog } from "./ImportDialog";
import { Action, Avatar, Chip, fileLink, h, niceDate, PersonCard, PreviewBar, StatusChip, todayIso } from "./kit";
import { HrError, SampleBanner, SUITE } from "./shared";

const EMOJI: Record<string, string> = {
  birthday: "🎂",
  work: "🎉",
  id_expiry: "🪪",
  passport_expiry: "🛂",
  contract_end: "📄",
  probation_end: "⏳",
  retirement: "🏁",
};

export function HrHomePage() {
  const session = useSession();
  const nav = useNavigate();
  const access = useHrAccess();
  const people = useHrPeople();
  const today = todayIso();
  const day = useHrAttendanceDay(today);
  const waiting = useHrRecords({ status: "requested" });
  const [q, setQ] = useState("");
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);
  const current = useMemo(() => (people.data?.employees ?? []).filter((e) => e.status !== "left"), [people.data]);
  const found = useMemo(() => {
    const n = q.trim().toLowerCase();
    if (!n) return [];
    return current
      .filter((e) =>
        `${e.name} ${e.employeeNo} ${e.jobTitle ?? ""} ${e.department ?? ""} ${e.idNumber ?? ""}`
          .toLowerCase()
          .includes(n),
      )
      .slice(0, 12);
  }, [q, current]);
  const dates = useMemo(() => comingUp(current, new Date(), 30).slice(0, 10), [current]);
  const counts = useMemo(() => {
    const c = { present: 0, late: 0, absent: 0, away: 0, none: 0 };
    for (const r of day.data?.rows ?? []) {
      if (!r.status) c.none++;
      else if (r.status === "present" || r.status === "remote") c.present++;
      else if (r.status === "late") c.late++;
      else if (r.status === "absent") c.absent++;
      else if (r.status === "leave" || r.status === "sick") c.away++;
    }
    return c;
  }, [day.data]);
  const requests = waiting.data?.records ?? [];
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";

  useScreenContext(
    people.data
      ? {
          route: "/hr",
          title: "Human Resources",
          summary: [
            t("{n} people on the register; {p} in today, {a} absent, {l} on leave.", {
              n: current.length,
              p: counts.present + counts.late,
              a: counts.absent,
              l: counts.away,
            }),
            t("{n} requests are waiting for a decision.", { n: requests.length }),
          ],
          offers: [
            { label: t("Who is absent today?"), ask: "who is absent today?" },
            { label: t("What is waiting for approval?"), ask: "which HR requests are waiting for approval?" },
          ],
          worksInFull: true,
        }
      : null,
  );

  return (
    <Page eyebrow={t("{site} · {suite}", { site: session.site.code, suite: t(SUITE) })} title="Human Resources">
      <PreviewBar />
      <SampleBanner />
      {people.error && <HrError error={people.error} retry={() => void people.refetch()} />}
      <section className={h.hero}>
        <h2>
          {t(greeting)}
          {session.principal?.name ? `, ${session.principal.name}` : ""} 👋
        </h2>
        <p>{t("Find anyone, see who's in, and handle what's waiting.")}</p>
        <input
          className={h.search}
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("Search by name, employee number, job or ID…")}
          aria-label={t("Search for a person")}
          data-testid="hr-home-search"
          onKeyDown={(e) => {
            if (e.key === "Enter" && found[0]) nav(fileLink(found[0].id));
          }}
        />
        {q.trim() && (
          <div className={h.people} data-testid="hr-home-results">
            {found.length ? (
              found.map((e) => <PersonCard key={e.id} e={e} />)
            ) : (
              <EmptyState>{t("Nobody matches. Try part of the name.")}</EmptyState>
            )}
          </div>
        )}
      </section>

      <div className={h.actions}>
        {access.can.write && (
          <Action
            icon="➕"
            label="Add a person"
            sub="New joiner"
            onClick={() => setAdding(true)}
            testId="hr-home-add"
          />
        )}
        {access.can.write && (
          <Action
            icon="📥"
            label="Import staff"
            sub="From the old system's spreadsheet"
            onClick={() => setImporting(true)}
            tint="var(--green)"
            testId="hr-home-import"
          />
        )}
        <Action
          icon="🗓"
          label="Take attendance"
          sub={counts.none ? t("{n} not recorded yet", { n: counts.none }) : "Everyone is recorded"}
          to="/hr-attendance"
          tint="var(--green)"
          count={counts.none || undefined}
        />
        <Action
          icon="✅"
          label="Approve requests"
          sub="Leave and letters"
          to="/hr-records?status=requested"
          tint="var(--amber)"
          count={requests.length || undefined}
        />
        <Action icon="👥" label="All staff" sub="The workforce register" to="/hr-people" />
        <Action
          icon="🧮"
          label="Calculators"
          sub="End of service, vacation, final pay"
          to="/hr-calculators"
          tint="#b07ce8"
        />
        <Action icon="📊" label="Reports" sub="Tables, charts, CSV, print" to="/hr-reports" tint="#3cc4c4" />
        <Action icon="🗂" label="Records registry" sub="Every file entry" to="/hr-records" />
        <Action icon="🔐" label="Access levels" sub="Who can see what" to="/hr-access" tint="var(--red)" />
      </div>

      <Card
        title={t("Today")}
        sub={niceDate(today)}
        actions={<Link to="/hr-attendance">{t("Open the attendance sheet")}</Link>}
      >
        {day.isLoading ? (
          <Skeleton rows={2} />
        ) : (
          <div className={h.stats}>
            {(
              [
                ["present", "In", counts.present, "var(--green)"],
                ["late", "Late", counts.late, "var(--amber)"],
                ["absent", "Absent", counts.absent, "var(--red)"],
                ["away", "On leave or sick", counts.away, "var(--blue)"],
                ["none", "Not recorded yet", counts.none, "var(--dim)"],
              ] as const
            ).map(([k, label, n, c]) => (
              <button
                key={k}
                type="button"
                className={h.stat}
                style={{ ["--tint" as string]: c }}
                onClick={() => nav(`/hr-attendance?show=${k}`)}
                data-testid={`hr-home-${k}`}
              >
                <span className={h.statN}>{n}</span>
                <span className={h.statLabel}>{t(label)}</span>
              </button>
            ))}
          </div>
        )}
      </Card>

      <div className={h.grid2}>
        <Card
          title={t("Waiting for a decision")}
          actions={<Link to="/hr-records?status=requested">{t("See all")}</Link>}
        >
          {waiting.isLoading ? (
            <Skeleton rows={3} />
          ) : requests.length === 0 ? (
            <EmptyState>{t("Nothing is waiting. 🎉")}</EmptyState>
          ) : (
            <ul className={h.list}>
              {requests.slice(0, 7).map((r) => (
                <li
                  key={r.id}
                  className={`${h.item} ${h.itemClick}`}
                  onClick={() => nav(fileLink(r.employeeId, r.kind === "leave" ? "leave" : "letters"))}
                >
                  <Avatar name={r.employeeName} size={32} />
                  <span className={h.itemMain}>
                    <b>{r.employeeName}</b>
                    <span>
                      {r.kind === "leave"
                        ? `${t(LEAVE_BY.get(leaveType(r))?.label ?? r.title)} · ${t("{n} days", { n: leaveDays(r) })} · ${niceDate(r.startsOn)}`
                        : t(r.title)}
                    </span>
                  </span>
                  <StatusChip status={r.status} />
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card title={t("Coming up in 30 days")} actions={<Link to="/hr-reports?report=dates">{t("Full list")}</Link>}>
          {dates.length === 0 ? (
            <EmptyState>{t("Nothing in the next 30 days.")}</EmptyState>
          ) : (
            <ul className={h.list}>
              {dates.map((x) => (
                <li
                  key={`${x.employeeId}-${x.kind}`}
                  className={`${h.item} ${h.itemClick}`}
                  onClick={() => nav(fileLink(x.employeeId, "dates"))}
                >
                  <span style={{ fontSize: 20 }} aria-hidden="true">
                    {EMOJI[x.kind]}
                  </span>
                  <span className={h.itemMain}>
                    <b>{x.name}</b>
                    <span>
                      {t(DATE_LABEL[x.kind])}
                      {x.years != null
                        ? ` · ${x.kind === "birthday" ? t("turns {n}", { n: x.years }) : t("{n} years", { n: x.years })}`
                        : ""}
                    </span>
                  </span>
                  <Chip colour={x.daysAway < 0 ? "var(--red)" : x.daysAway <= 7 ? "var(--amber)" : undefined}>
                    {x.daysAway < 0
                      ? t("{n} days ago", { n: -x.daysAway })
                      : x.daysAway === 0
                        ? t("Today")
                        : t("in {n} days", { n: x.daysAway })}
                  </Chip>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <Card title={t("Away today")}>
        {(() => {
          const away = (day.data?.rows ?? []).filter(
            (r) => r.status === "leave" || r.status === "sick" || r.status === "absent",
          );
          return away.length === 0 ? (
            <EmptyState>{t("Everybody recorded is in.")}</EmptyState>
          ) : (
            <div className={h.people}>
              {away.map((r) => (
                <PersonCard
                  key={r.employeeId}
                  e={{ id: r.employeeId, name: r.name, jobTitle: null, department: r.department }}
                  extra={
                    <Chip colour={ATTENDANCE_BY.get(r.status as "leave")?.colour}>
                      {t(ATTENDANCE_BY.get(r.status as "leave")?.label ?? "")}
                    </Chip>
                  }
                />
              ))}
            </div>
          );
        })()}
      </Card>
      {adding && <EmployeeDialog onClose={() => setAdding(false)} />}
      {importing && <ImportDialog onClose={() => setImporting(false)} />}
    </Page>
  );
}
